import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import type {DeferredCardFieldsSource} from '../components/board/issues/deferred-card-fields'
import {isBoardDeferredPayload, type BoardDeferredFields} from '../components/board/issues/deferred-payload'
import {createBatchLoader} from './create-batch-loader'

/**
 * Long enough to gather every card a board draws in one frame into one request, and short
 * enough that the values arrive with the board rather than after it.
 */
const BATCH_WINDOW_MS = 50

// Mirrors the server's cap on `board-deferred` ids, which refuses a request naming more.
const MAX_IDS_PER_REQUEST = 50

export function createSavedViewDeferredFieldsSource({
  pathname,
  routeId,
  scope,
}: {
  pathname: string
  routeId: string
  /** Identifies the view, not its response, so a board refresh keeps each item's cached fields. */
  scope: DeferredCardFieldsSource['scope']
}): DeferredCardFieldsSource {
  const {load} = createBatchLoader({
    fetch: globalIds => fetchDeferredFields(pathname, routeId, globalIds),
    batchWindowMs: BATCH_WINDOW_MS,
    maxBatchSize: MAX_IDS_PER_REQUEST,
  })

  return {scope, load}
}

async function fetchDeferredFields(
  pathname: string,
  routeId: string,
  globalIds: string[],
): Promise<Map<string, BoardDeferredFields>> {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The board deferred endpoint returns plain JSON validated by `isBoardDeferredPayload`, not a Protobuf payload.
  const json = await queryFnFetch<unknown>({
    routeId,
    queryName: 'boardDeferredFields',
    queryDeps: {pathname, searchParams: {ids: globalIds.join(',')}},
  })

  if (!isBoardDeferredPayload(json)) throw new Error('board deferred fields failed validation')

  return new Map(json.items.map(item => [item.globalId, item.fields]))
}
